import {
  add,
  angleOf,
  arc,
  cross,
  dist,
  fromPolar,
  normalizeDegrees,
  scale,
  sub,
  vec,
  type Vec2,
} from '../geom';
import { formatDecimal, type AngleDimensionNode, type XY } from '../model';
import { arrowhead, extension, gapOf, joined, readable, textAt } from './parts';
import type { DimensionDefinition } from './types';

/** A ray from the vertex out along a line, toward its end farther from the vertex. */
interface Ray {
  readonly angle: number;
  readonly reach: number;
}

function rayOf(vertex: Vec2, line: { readonly a: XY; readonly b: XY }): Ray {
  const far = dist(vertex, line.a) > dist(vertex, line.b) ? line.a : line.b;
  return { angle: angleOf(sub(far, vertex)), reach: dist(vertex, far) };
}

/** Where the two lines' carriers cross, or null when they are parallel. */
function vertexOf(node: AngleDimensionNode): Vec2 | null {
  const { first, second } = node;
  const d1 = sub(first.b, first.a);
  const d2 = sub(second.b, second.a);
  const denominator = cross(d1, d2);
  if (Math.abs(denominator) <= 1e-12 * Math.hypot(d1.x, d1.y) * Math.hypot(d2.x, d2.y)) return null;
  return add(first.a, scale(d1, cross(sub(second.a, first.a), d2) / denominator));
}

/** The angle between two lines: the sector of at most 180 degrees that both rays bound. */
export const ANGLE: DimensionDefinition<AngleDimensionNode> = {
  label: 'Angle dimension',
  arity: 10,
  fromRecord: ([ax, ay, bx, by, cx, cy, dx, dy, radius, placed], kind) => ({
    kind,
    first: { a: vec(ax!, ay!), b: vec(bx!, by!) },
    second: { a: vec(cx!, cy!), b: vec(dx!, dy!) },
    radius: placed ? radius! : null,
  }),
  resolve(node, look) {
    const vertex = vertexOf(node);
    if (!vertex) return 'An angle dimension needs two lines that are not parallel';
    let from = rayOf(vertex, node.first);
    let to = rayOf(vertex, node.second);
    if (from.reach === 0 || to.reach === 0)
      return 'An angle dimension needs lines that reach past where they meet';
    let sweep = normalizeDegrees(to.angle - from.angle);
    if (sweep > 180) {
      [from, to] = [to, from];
      sweep = 360 - sweep;
    }
    const radius = node.radius ?? Math.min(from.reach, to.reach) / 2;
    const start = fromPolar(vertex, radius, from.angle);
    const end = fromPolar(vertex, radius, from.angle + sweep);
    const ends = joined([
      arrowhead(start, fromPolar(vec(0, 0), 1, from.angle - 90), look),
      arrowhead(end, fromPolar(vec(0, 0), 1, from.angle + sweep + 90), look),
    ]);
    const middle = from.angle + sweep / 2;
    const reaches = (ray: Ray, at: Vec2) =>
      ray.reach < radius ? extension(fromPolar(vertex, ray.reach, ray.angle), at, look) : [];
    return {
      value: sweep,
      layout: {
        strokes: [
          arc(vertex, radius, from.angle, from.angle + sweep),
          ...reaches(from, start),
          ...reaches(to, end),
          ...ends.strokes,
        ],
        fills: ends.fills,
        text: textAt(
          fromPolar(vertex, radius + gapOf(look) + look.textHeight / 2, middle),
          readable(middle - 90),
          `${formatDecimal(sweep, look.display.precision)}°`,
          look,
        ),
      },
    };
  },
};
